import AppCard from '@nxsys/components/AppCard';
import AppLoader from '@nxsys/components/AppLoader';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { PROSPECT_DEFAULT_PAGE_SIZE, PROSPECT_ONBOARDING_TABLE_SCROLL, PROSPECT_TYPE } from '@nxsys/constants/prospect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { socketOnboarding } from '@nxsys/services/sockets';
import { AppDispatch, AppState } from '@nxsys/store';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import {
  GetProspectListParams,
  Prospect,
} from '@nxsys/store/slices/prospect/types';
import { showToast } from '@nxsys/utils/toast';
import { Modal } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionsHeader } from '../ActionsHeader';
import { getColumns } from '../columns';
import ViewProspectModal from './ViewProspectModal';
import { filter, some } from 'lodash';
import ViewOnboardingModal from '../Onboarding/ViewOnboardingModal';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';

type ProspectTabProps = {
  onAddNew?: () => void;
  renderAddNewButton: (variant: string) => React.ReactNode;
};

const ProspectTab = ({ onAddNew, renderAddNewButton }: ProspectTabProps) => {
  const { messages } = useTypedIntl();

  const { isBureau, payrollCountry, isAgency, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;

  const dispatch = useDispatch<AppDispatch>();
  const { prospectList } = useSelector(
    (state: AppState) => state.prospectStore,
  );
  const { loading, list, pagination, queryParams } = prospectList;

  const [selectedRows, setSelectedRows] = useState<Prospect[]>([]);

  const [viewProspectModal, setViewProspectModal] = useState<{
    open: boolean;
    data: Prospect | null;
  }>({
    open: false,
    data: null,
  });
  const toggleViewProspectModal = () =>
    setViewProspectModal({
      ...viewProspectModal,
      open: !viewProspectModal.open,
    });

  const [viewOnboardingModal, setViewOnboardingModal] = useState<{
    open: boolean;
    data: Prospect | null;
  }>({
    open: false,
    data: null,
  });
  const toggleViewOnboardingModal = () =>
    setViewOnboardingModal({
      ...viewOnboardingModal,
      open: !viewOnboardingModal.open,
    });

  const [isProgressRegistration, setIsProgressRegistration] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: PROSPECT_DEFAULT_PAGE_SIZE,
    });
  }, []);

  // FE-BUG-001: Use functional setState to avoid stale closure
  useEffect(() => {
    setSelectedRows((prev) => {
      const newSelectedRow: Prospect[] = [];
      prev.forEach((item) => {
        const index = list.findIndex((i) => i.id === item.id);
        if (index !== -1) {
          newSelectedRow.push(list[index]);
        }
      });
      return newSelectedRow;
    });
  }, [list]);

  const handleFetchData = (params: GetProspectListParams) => {
    const newParams = { ...params };
    newParams.registrationStatus = 'PENDING';
    dispatch(prospectActions.getProspectList(newParams));
  };

  const refetchData = () => handleFetchData({ ...queryParams });

  // The sidebar badge is fetched again on every new-onboarding push (an
  // invite, import or bulk update opening prospects, or an employee finishing
  // onboarding), so the list it counts is too, or the two disagree while the
  // tab is open.
  const refetchDataRef = useRef(refetchData);
  refetchDataRef.current = refetchData;
  useEffect(() => {
    if (!user?.companyParentId) return;
    const event = `new-onboarding-${user.companyParentId}`;
    const handler = () => refetchDataRef.current();
    socketOnboarding.on(event, handler);
    return () => {
      socketOnboarding.off(event, handler);
    };
  }, [user?.companyParentId]);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  // The BE only sorts when it gets both halves, so they are cleared together
  const onChangeTable = (sorter?: {
    field: string;
    order: 'desc' | 'asc';
  }) => {
    handleFetchData({
      ...queryParams,
      page: 0,
      sortField: sorter?.field || undefined,
      sortType: sorter?.order || undefined,
    });
  };

  const handleViewProspect = (data: Prospect) => {
    setViewProspectModal({
      open: true,
      data,
    });
  };

  // FE-BUG-002: Wrap in try/finally, FE-BUG-003: types already correct (number[])
  const handleProgressRegistrationByIds = async (ids: number[]) => {
    const data = list.filter((item) => ids.includes(item.id));
    // UK bureau has no payment model to pick — it is always PAYE
    // FE-BUG-008: Changed from !== 1 to < 1 — allow multiple payment models
    const checkPaymentModalAndCompanyPayment = some(
      data,
      (item) => (!isUK && !item?.paymentModel?.length) || !item.companyPayment,
    );
    if (checkPaymentModalAndCompanyPayment) {
      showToast({
        type: 'warning',
        // The company field is labelled "Employer" for bureau, "Payment Company" otherwise
        message:
          messages[
            isUK
              ? 'prospect.onboarding.pleaseUpdateEmployer'
              : isBureau
                ? 'prospect.onboarding.pleaseUpdatePaymentModalAndEmployer'
                : 'prospect.onboarding.pleaseUpdatePaymentModalAndPaymentCompany'
          ],
      });
      return;
    }
    const progressed = filter(data, (row) => !row?.userRef);
    if (!progressed.length) {
      showToast({
        type: 'warning',
        message: messages['prospect.onboarding.allProspectsWereProgressed'],
      });
      return;
    }

    setIsProgressRegistration(true);
    try {
      const params = { ids };
      await dispatch(prospectActions.progressProspectByIds(params)).unwrap();
      refetchData();
    } catch {
      showToast({ message: messages['common.errorOccurred'] ?? 'An error occurred', type: 'error' });
    } finally {
      setIsProgressRegistration(false);
    }
  };

  const handleProgressRegistrationSelected = () => {
    if (!selectedRows || selectedRows.length === 0) {
      showToast({
        message: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }
    handleProgressRegistrationByIds(selectedRows.map((row) => row.id));
  };

  const handleResendByIds = async (ids: number[]) => {
    setIsResending(true);
    try {
      const params = { ids };
      await dispatch(prospectActions.resendProspectByIds(params)).unwrap();
      refetchData();
    } catch {
      showToast({ message: messages['common.errorOccurred'] ?? 'An error occurred', type: 'error' });
    } finally {
      setIsResending(false);
    }
  };

  const handleResendByIdsSelected = () => {
    if (!selectedRows || selectedRows.length === 0) {
      showToast({
        message: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }
    handleResendByIds(selectedRows.map((row) => row.id));
  };

  // FE-BUG-004: Add confirmation dialog before delete
  const handleDeleteProspectById = async (id: number) => {
    Modal.confirm({
      title: messages['common.confirm'] || 'Confirm',
      content: messages['prospect.onboarding.confirmDelete'] || 'Are you sure you want to delete this prospect?',
      onOk: async () => {
        setIsDeleting(true);
        try {
          const params = { id, isIreland };
          await dispatch(prospectActions.deleteProspectById(params)).unwrap();
          refetchData();
        } catch {
          showToast({ message: messages['common.errorOccurred'] ?? 'An error occurred', type: 'error' });
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  const handleDeleteProspectSelected = async () => {
    if (!selectedRows || selectedRows.length === 0) {
      showToast({
        message: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }
    Modal.confirm({
      title: messages['common.confirm'] || 'Confirm',
      content: `Are you sure you want to delete ${selectedRows.length} prospect(s)?`,
      onOk: async () => {
        setIsDeleting(true);
        try {
          const params = {
            ids: selectedRows.map((row) => row.id),
            isIreland,
          };
          await dispatch(prospectActions.deleteProspectByIds(params)).unwrap();
          refetchData();
        } catch {
          showToast({ message: messages['common.errorOccurred'] ?? 'An error occurred', type: 'error' });
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  const handleViewOnboarding = (data: Prospect) => {
    setViewOnboardingModal({
      open: true,
      data,
    });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.companyIds?.length ||
    !!queryParams?.agencyIds?.length ||
    !!queryParams?.paymentModel;

  return (
    <AppCard>
      {(isProgressRegistration || isResending || isDeleting) && <AppLoader />}
      <AppsHeader>
        <ActionsHeader
          fetchData={handleFetchData}
          type={PROSPECT_TYPE.ONBOARDING}
          onProgressRegistrationSelected={handleProgressRegistrationSelected}
          onResendByIdsSelected={handleResendByIdsSelected}
          onDeleteProspectSelected={handleDeleteProspectSelected}
          onFilter={handleFetchData}
          onAddNew={onAddNew}
        />
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={PROSPECT_ONBOARDING_TABLE_SCROLL}
          titleEmpty={messages['prospect.onboarding.empty'] ?? messages['prospect.candidate.empty']}
          columns={getColumns({
            messages,
            prospectType: PROSPECT_TYPE.ONBOARDING,
            handleViewProspect,
            handleResendByIds,
            handleDeleteProspectById,
            handleViewOnboarding,
            handleProgressRegistrationByIds,
            isBureau,
            isUK,
            isIreland,
            isAgency,
          })}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
          rowSelection={{
            type: 'checkbox',
            onChange: (_selectedRowKeys: React.Key[], selectedRows: Prospect[]) => {
              setSelectedRows(selectedRows);
            },
          }}
          noDataType={isFiltering ? 'noDataFound' : 'noDataCreated'}
          addNewBtn={renderAddNewButton('outlined')}
        />
      </AppsContent>
      <ViewProspectModal
        isOpen={viewProspectModal.open}
        toggle={toggleViewProspectModal}
        data={viewProspectModal.data}
        refetchData={refetchData}
        onResendByIds={handleResendByIds}
        onViewOnboarding={handleViewOnboarding}
        onDeleteProspectById={handleDeleteProspectById}
      />
      {viewOnboardingModal.open && viewOnboardingModal.data?.id && (
        <ViewOnboardingModal
          isOpen={viewOnboardingModal.open}
          toggle={toggleViewOnboardingModal}
          prospectId={viewOnboardingModal.data.id}
          refetchData={refetchData}
        />
      )}
    </AppCard>
  );
};

export default ProspectTab;
